import { useEffect, useRef } from 'react'

// Grid cell constants (px)
const CELL   = 6
const MARGIN = 1
const DOT    = CELL - MARGIN * 2   // 4 px visible dot

const PERIOD   = 30_000            // 30 s full rotation
const THROTTLE = 1000 / 9          // ~9 fps (Cloudflare pace)
const TILT_DEG = -12               // slight axial tilt
const RAD      = Math.PI / 180

// Land bounding boxes [latMin, latMax, lonMin, lonMax]
const REGIONS = [
    [60, 80, -168, -60], [45, 60, -140, -55], [25, 50, -128, -65],
    [15, 28, -112, -86], [ 7, 15,  -90, -76], [60, 85,  -58, -16],
    [19, 24,  -85, -68],
    [ 0, 13,  -82, -50], [-15,  0,  -82, -35], [-35,-15,  -75, -40], [-56,-35,  -76, -62],
    [55, 72,   -8,  32], [43, 55,   -8,  32], [36, 43,   -9,   5],
    [37, 43,   14,  28], [37, 42,   26,  42], [50, 61,  -10,   2],
    [63, 67,  -24, -13], [74, 81,   10,  33],
    [20, 38,  -18,  40], [ 0, 20,  -18,  52], [-35,  0,   10,  42], [-26,-12,   43,  51],
    [12, 38,   32,  62],
    [50, 80,   28, 100], [50, 80,  100, 175], [ 8, 55,   60, 142],
    [34, 42,  125, 132], [31, 46,  129, 148],
    [-8,  8,   95, 120], [-8,  2,  108, 128], [-5,  7,  114, 128],
    [-10, 0,  130, 150], [ 8, 20,  118, 128],
    [-44,-10,  113, 155], [-47,-34,  165, 179],
    [-90,-65, -180, 178],
]

// Deterministic per-cell pseudo-random — fixed noise, creates organic blink
function cellNoise(x, y) {
    const n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453
    return n - Math.floor(n)
}

// Animated threshold: base ocean haze + land pulsing via dual sine waves
function threshold(gx, gy, t, alpha) {
    const wave = (Math.sin(gx * 0.27 + t * 2e-4) + Math.sin(gy * 0.19 - t * 15e-5) + 2) / 4
    return 0.05 + alpha * (0.42 + wave * 0.28)
}

function isLand(lon, lat) {
    for (const [la, lb, lo, lp] of REGIONS)
        if (lat >= la && lat <= lb && lon >= lo && lon <= lp) return true
    return false
}

// Inverse orthographic projection: grid offset (dx, dy) → geographic (lon, lat)
function unproject(dx, dy, r, lonDeg) {
    const nx = dx / r, ny = -dy / r
    const rho2 = nx * nx + ny * ny
    if (rho2 > 1) return null
    const rho  = Math.sqrt(rho2)
    const tiny = rho < 1e-10
    const c    = tiny ? 0 : Math.asin(rho)
    const phi0 = TILT_DEG * RAD
    const sinC = Math.sin(c), cosC = Math.cos(c)
    const sinP = Math.sin(phi0), cosP = Math.cos(phi0)
    const lat  = Math.asin(Math.max(-1, Math.min(1, cosC * sinP + (tiny ? 0 : ny * sinC * cosP / rho))))
    const lon  = lonDeg * RAD + (tiny ? 0 : Math.atan2(nx * sinC, rho * cosC * cosP - ny * sinC * sinP))
    return [((lon / RAD + 180) % 360 + 360) % 360 - 180, lat / RAD]
}

export default function GlobeCanvas({ size = 600, style = {} }) {
    const canvasRef = useRef(null)
    const rafRef    = useRef(null)
    const lastRef   = useRef(0)

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        const dpr = Math.min(window.devicePixelRatio || 1, 2)
        canvas.width  = size * dpr
        canvas.height = size * dpr
        canvas.style.width  = `${size}px`
        canvas.style.height = `${size}px`
        ctx.scale(dpr, dpr)

        const GW = Math.ceil(size / CELL)
        const GH = Math.ceil(size / CELL)
        const CX = GW / 2
        const CY = GH / 2
        const R  = Math.min(GW, GH) * 0.47
        const R2 = R * R

        function draw(t) {
            if (t - lastRef.current < THROTTLE) { rafRef.current = requestAnimationFrame(draw); return }
            lastRef.current = t

            ctx.clearRect(0, 0, size, size)
            const lonDeg = -18 + (t / PERIOD) * 360

            ctx.fillStyle = 'rgba(247,148,29,0.92)'

            for (let gy = 0; gy < GH; gy++) {
                for (let gx = 0; gx < GW; gx++) {
                    const dx = gx + 0.5 - CX
                    const dy = gy + 0.5 - CY
                    if (dx * dx + dy * dy > R2) continue

                    const coords = unproject(dx, dy, R, lonDeg)
                    if (!coords) continue

                    const alpha = isLand(coords[0], coords[1]) ? 1 : 0
                    if (cellNoise(gx, gy) <= threshold(gx, gy, t, alpha))
                        ctx.fillRect(gx * CELL + MARGIN, gy * CELL + MARGIN, DOT, DOT)
                }
            }

            rafRef.current = requestAnimationFrame(draw)
        }

        rafRef.current = requestAnimationFrame(draw)
        return () => cancelAnimationFrame(rafRef.current)
    }, [size])

    return <canvas ref={canvasRef} style={{ display: 'block', ...style }} />
}
